import type { PageSize, Tool } from '../../model/types';
import { buildOutline, polygonBounds, tracePolygon } from './geometry';
import { HIGHLIGHTER_ALPHA } from './TileCache';

export interface LiveStyle {
  tool: Tool;
  color: string;
  baseWidth: number;
}

interface DirtyRect { x: number; y: number; w: number; h: number }

/**
 * Draws the stroke in progress directly onto the opaque display canvas.
 * Before each redraw the previously covered area is restored from the
 * committed back buffer (via `restore`), so no transparent overlay canvas is
 * needed. Transparent desynchronized canvases render black on some Android
 * GPUs; an opaque one is the supported low-latency configuration.
 */
export class LiveLayer {
  private dirty: DirtyRect | null = null;
  private last: (() => void) | null = null;

  constructor(
    private ctx: CanvasRenderingContext2D,
    private restore: (x: number, y: number, w: number, h: number) => void,
  ) {}

  /** Redraws the whole live stroke. Returns elapsed ms. */
  draw(points: Float32Array, extra: number[], style: LiveStyle, origin: { x: number; y: number }, scale: number, page: PageSize): number {
    const t0 = performance.now();
    this.last = () => this.paint(points, extra, style, origin, scale, page);
    this.clear();
    this.paint(points, extra, style, origin, scale, page);
    return performance.now() - t0;
  }

  private paint(points: Float32Array, extra: number[], style: LiveStyle, origin: { x: number; y: number }, scale: number, page: PageSize): void {
    const poly = buildOutline(points, style, extra);
    if (poly.length === 0) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(scale, 0, 0, scale, origin.x, origin.y);
    ctx.beginPath();
    ctx.rect(0, 0, page.width, page.height);
    ctx.clip();
    ctx.beginPath();
    tracePolygon(ctx, poly);
    ctx.globalAlpha = style.tool === 'highlighter' ? HIGHLIGHTER_ALPHA : 1;
    ctx.fillStyle = style.color;
    ctx.fill('nonzero');
    ctx.restore();
    const b = polygonBounds(poly);
    this.dirty = {
      x: Math.floor(origin.x + b.minX * scale) - 2,
      y: Math.floor(origin.y + b.minY * scale) - 2,
      w: Math.ceil((b.maxX - b.minX) * scale) + 5,
      h: Math.ceil((b.maxY - b.minY) * scale) + 5,
    };
  }

  /** Eraser-mode feedback ring (no data is recorded). */
  drawRing(cx: number, cy: number, r: number): void {
    this.last = () => this.paintRing(cx, cy, r);
    this.clear();
    this.paintRing(cx, cy, r);
  }

  private paintRing(cx: number, cy: number, r: number): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(80,80,90,0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
    this.dirty = { x: cx - r - 3, y: cy - r - 3, w: 2 * r + 6, h: 2 * r + 6 };
  }

  /** Restores the area under the live stroke from the committed buffer. */
  clear(): void {
    if (!this.dirty) return;
    const d = this.dirty;
    this.dirty = null;
    this.restore(d.x, d.y, d.w, d.h);
  }

  /** The display was fully repainted from the back buffer: redraw the live stroke on top if one is active. */
  repaintAfterBlit(active: boolean): void {
    this.dirty = null;
    if (active) this.last?.();
    else this.last = null;
  }

  /** Stroke finished and committed: forget it. */
  reset(): void {
    this.dirty = null;
    this.last = null;
  }
}
